import { produce } from 'immer';

import { CallToAction, CallToActionType, PageLinkType } from '@/types/homepage.types';
import {
  RegistrationQuestion,
  RegFieldsEnum,
  QuestionType,
  RegistrationStepType,
  RegistrationStep,
  RegistrationTypes,
  Registration,
} from '@/types/registration.types';

export function GetDefaultCallToAction(registrationOn: boolean): CallToAction {
  const buttonName = registrationOn ? 'Registration' : 'Join Event';
  const pageLink = registrationOn ? PageLinkType.registration : PageLinkType.homepage;

  return {
    type: CallToActionType.page,
    button_name: { base: buttonName, changed: '' },
    content: {
      isOn: true,
      page: pageLink,
    },
  };
}

export const GetDefaultRegistrationQuestions = (): RegistrationQuestion[] => [
  {
    registration_question: RegFieldsEnum.first_name,
    channel: 0,
    name: 'First Name',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: true,
    default_value: null,
  },
  {
    registration_question: RegFieldsEnum.last_name,
    channel: 0,
    name: 'Last Name',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: true,
    default_value: null,
  },
  {
    registration_question: RegFieldsEnum.email,
    channel: 0,
    name: 'Email Address',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: true,
    default_value: null,
  },
  // Commented out until we know if we are including enable messaging in registration
  // GetDefaultEnableMessagingQuestion()
];

export const GetDefaultProfileQuestions = (): RegistrationQuestion[] => [
  {
    registration_question: RegFieldsEnum.company_name,
    channel: 0,
    name: 'Company Name',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: true,
    default_value: null,
  },
  {
    registration_question: RegFieldsEnum.bio,
    channel: 0,
    name: 'Bio',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: false,
    default_value: null,
  },
  {
    registration_question: RegFieldsEnum.fun_fact,
    channel: 0,
    name: 'Fun Fact',
    type: QuestionType.text,
    options: null,
    global: true,
    persistent: false,
    default_value: null,
  },
];

export const GetDefaultRegistrationSteps = (): RegistrationStep[] => {
  return [
    {
      type: RegistrationStepType.general,
      isOn: true,
      questions: GetDefaultRegistrationQuestions(),
    },
    {
      type: RegistrationStepType.profile,
      isOn: false,
      questions: GetDefaultProfileQuestions(),
    },
    {
      type: RegistrationStepType.avatar,
      isOn: false,
    },
  ];
};

/**
 * Because our registration questions are sortable, we need to make sure that our event.registration_questions
 * correctly match the order of the registration steps questions.
 * This function is used to sort the redux registration questions based on the order of the registration steps questions
 * We need to ensure that the order of workingEvent.registration_questions matches the combined order of
 * general and profile registration steps questions, respectively.
 * Once sorted, redux will save the correct order of registration questions into the db.
 * @param registrationSteps sorted redux registration steps
 * @param registrationQuestions redux workingEvent.registration_questions
 * @param registrationQuestionsToAppend if we are adding a new registration question, we need to append it to the correct step
 * @returns sorted registration questions
 */
export const sortWorkingEventRegistrationQuestions = ({
  registrationSteps,
  registrationQuestions,
  registrationQuestionsToAppend,
}: {
  registrationSteps?: RegistrationStep[];
  registrationQuestions?: RegistrationQuestion[];
  registrationQuestionsToAppend?: {
    step: RegistrationStepType;
    questions: RegistrationQuestion[];
  };
}): RegistrationQuestion[] => {
  // updatedRegistrationQnOrder stores our properly sorted registration questions
  const updatedRegistrationQnOrder: RegistrationQuestion[] = [];

  // get all general step question ids
  const alreadySortedGeneralStepQuestions =
    registrationSteps
      ?.find((step) => step.type === RegistrationStepType.general)
      ?.questions?.map((q) => q.registration_question) || [];

  // get all profile step questions ids
  const alreadySortedProfileStepQuestions =
    registrationSteps
      ?.find((step) => step.type === RegistrationStepType.profile)
      ?.questions?.map((q) => q.registration_question) || [];

  // The order below matters:
  // `alreadySortedGeneralStepQuestions` must come before `alreadySortedProfileStepQuestions`

  // for each general step question, find the corresponding registration question and add it to the sort array
  // so that we create the correct order of registration questions
  alreadySortedGeneralStepQuestions.forEach((q) => {
    const found = registrationQuestions?.find((rq) => rq.registration_question === q);
    if (found) updatedRegistrationQnOrder.push(found);
  });
  // if we are adding a new general step registration question, we need to append it to the end of the general step
  if (registrationQuestionsToAppend?.step === RegistrationStepType.general) {
    updatedRegistrationQnOrder.push(...registrationQuestionsToAppend.questions);
  }

  // begin profile step questions
  // for each profile step question, find the corresponding registration question and add it to the sort array
  alreadySortedProfileStepQuestions.forEach((q) => {
    const found = registrationQuestions?.find((rq) => rq.registration_question === q);
    if (found) updatedRegistrationQnOrder.push(found);
  });
  // if we are adding a new profile step registration question, we need to append it to the end of the profile step
  if (registrationQuestionsToAppend?.step === RegistrationStepType.profile) {
    updatedRegistrationQnOrder.push(...registrationQuestionsToAppend.questions);
  }

  // this will become our new workingEvent.registration_questions, which redux will save to the db
  // note that only the question ids actually get saved into the db as number[],
  // but we need the full RegistrationQuestion[] for redux
  return updatedRegistrationQnOrder;
};

/**
 * Sorts a list of RegistrationStep so that ticketing is last
 * If the list is undefined or less than one return early.
 */
export const sortRegistrationSteps = (
  registrationSteps?: Array<RegistrationStep>
): Array<RegistrationStep> | undefined => {
  if (!registrationSteps || registrationSteps.length <= 1) {
    return registrationSteps;
  }
  // produce gives us an immutable update without round-tripping through JSON, so any
  // non-serializable fields on the steps (Dates, Maps, etc.) survive the copy intact.
  return produce(registrationSteps, (draft) => {
    const ticketingIdx = draft.findIndex((r) => r.type === RegistrationStepType.ticketing);
    if (ticketingIdx === -1) return;
    draft.push(...draft.splice(ticketingIdx, 1));
  });
};

/**
 * Returns the list of registration questions from the registration steps
 */
export const getRegistrationQuestionsFromRegistrationSteps = (
  registrationSteps?: Array<RegistrationStep>
): Array<RegistrationQuestion> => {
  if (!registrationSteps) return [];

  const registrationQuestions =
    registrationSteps?.reduce((questions: RegistrationQuestion[], step: RegistrationStep): RegistrationQuestion[] => {
      if (step?.questions) {
        questions.push(...step.questions);
      }
      return questions;
    }, []) ?? [];

  return registrationQuestions;
};

export const GetDefaultRegistrationSettings = (): Registration => ({
  type: RegistrationTypes.none,
  enableSocial: false,
  enableRecaptcha: false,
  enableOverviewContent: true,
  enableSpeakers: true,
  singleSignOn: {
    isOn: false,
    displayTop: false,
    singleSignOns: [],
  },
  sessionReminders: true,
  registrationConfirmation: true,
  sendEmailConfirmation: true,
  registration_confirmation_email: {},
  postEventFeedbackSurvey: false,
  useBlockedDomainEmails: false,
  blockedDomainEmails: [],
  requirements: {},
});
